/**
 * Cache-direct METADATA-STUB detection — the ONE truth, next to its producer.
 *
 * # What a stub is
 *
 * On the cache-direct (huge-drawing) path the spatial index's entity resolver
 * is `renderableById` CHAINED with a metadata-stub fallback
 * (`spatialBuilder.ts createMetadataStubResolver`, installed by
 * `scene/SceneBulkLoad.ts finishSceneBulkLoad`). An id the LOD pre-classify
 * dropped as sub-pixel — i.e. one the renderer NEVER lifted and therefore draws
 * NO geometry for — resolves to a STUB built by `metadataToSpatialStub`
 * (`spatialBuilder.ts`, this module's sibling):
 *
 *   - `geometry.kind === "point"`, `position` = the entity's bbox CENTRE,
 *     `pdmode`/`pdsize` = 0;
 *   - the entity's REAL `id` / `type` (`"Line"`, `"Circle"`, `"Block"`,
 *     `"MText"`, …) / `layer` / `bounds`.
 *
 * A genuine DXF POINT is the ONLY renderable whose geometry is legitimately
 * `kind: "point"`, and it keeps `type === "Point"`
 * (`adapters/sceneGeometryBuilders.ts buildPointSceneGeometry` is reached only
 * from `pointToRenderable` / `typed/pointFromEntity`). So `kind === "point"`
 * with any OTHER `type` is a placeholder whose single `geom.position` does NOT
 * describe real geometry — only `bounds` does.
 *
 * # Why this lives here, as a leaf
 *
 * The predicate is a property of the stub FORMAT, so its home is beside the
 * producer (`spatialBuilder.ts metadataToSpatialStub`). It is a LEAF module
 * rather than an export of `spatialBuilder.ts` itself so that consumers do not
 * pay that file's runtime dependency subtree (cad-core, the typed adapters,
 * `scene/liftBlockInsert`, `coordinateOrigin`) for a one-line type test: this
 * module's only import is TYPE-ONLY, so it adds no runtime edge to any
 * consumer (dep-graph discipline, CLAUDE.md PRIORITY #2).
 *
 * # Consumers
 *
 *   - `snap/osnapCandidates.ts` — suppresses NODe (and every other kind-`point`
 *     candidate) for a stub, so OSNAP never marks a spot where nothing is
 *     drawn. See the `case "point"` guard there.
 *   - `raycast/windowSelect.ts` — a stub's lone `geom.position` is the bbox
 *     centre, so window/crossing tests must run against the mapped bbox
 *     CORNERS instead. See `testPoints` there.
 *
 * # NOT the same predicate as `scene/refinedEntityPick.ts isStub`
 *
 * That one is DELIBERATELY WIDER — "any single-point renderable that might
 * need a cache lift", which INCLUDES a genuine POINT with a non-degenerate
 * PDSIZE marker bbox. It is a cheap over-approximation whose only cost is a
 * redundant lift. Merging the two would suppress NODe on real DXF POINTs
 * (this predicate is a correctness gate, not a cost heuristic), so the two must
 * stay separate. See the cross-reference comment at `isStub`'s definition.
 */

import type { RenderableEntity } from "../types";

/**
 * True iff `entity` is a cache-direct metadata stub — a `point`-geometry
 * placeholder standing in for an entity the renderer never lifted, so it
 * carries no real geometry.
 *
 * A genuine DXF POINT (`type === "Point"`) is NOT a stub: its
 * `geometry.position` IS the node AutoCAD snaps to, whether the record came
 * from the adapters or from the metadata fallback (`metadataToSpatialStub`
 * puts the bbox centre there, and a POINT's marker bbox is centred on its
 * insertion point).
 */
export function isMetadataStubRenderable(entity: RenderableEntity): boolean {
  return entity.geometry.kind === "point" && entity.type !== "Point";
}
